/**
 * <clamped-text lines="2"> — text cut to a few lines by CSS (-webkit-line-clamp on the content) with a toggle that
 * shows only when the text is longer. The toggle expands it in place and collapses it again; the state is the
 * `expanded` attribute on the host (for CSS) and aria-expanded on the toggle.
 *
 *   <clamped-text lines="2">
 *     <div id="…" data-clamped-text-content>…</div>
 *     <button type="button" aria-controls="…" aria-expanded="false" data-clamped-text-toggle hidden>…</button>
 *   </clamped-text>
 *
 * `lines` must match the CSS clamp. The content may be written after load (the sticky bar's delivery copy comes from
 * <delivery-info>) and changes on variant change, so the length check reruns when the content mutates or resizes.
 */
class ClampedText extends HTMLElement {
  #content = null;
  #toggle = null;
  #observers = [];
  #frame = 0;

  connectedCallback() {
    this.#content = this.querySelector('[data-clamped-text-content]');
    this.#toggle = this.querySelector('[data-clamped-text-toggle]');
    if (!this.#content || !this.#toggle) return;

    this.#toggle.addEventListener('click', this.#onToggle);

    const resizeObserver = new ResizeObserver(this.#scheduleCheck);
    resizeObserver.observe(this.#content);
    const mutationObserver = new MutationObserver(this.#scheduleCheck);
    mutationObserver.observe(this.#content, { childList: true, characterData: true, subtree: true });
    this.#observers = [resizeObserver, mutationObserver];

    this.#scheduleCheck();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    cancelAnimationFrame(this.#frame);
  }

  #onToggle = () => {
    this.#setExpanded(!this.hasAttribute('expanded'));
  };

  #setExpanded(expanded) {
    this.toggleAttribute('expanded', expanded);
    this.#toggle.setAttribute('aria-expanded', String(expanded));
  }

  #scheduleCheck = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#check);
  };

  // scrollHeight is the full text height whether the lines are clamped or not.
  #check = () => {
    const lines = Number(this.getAttribute('lines')) || 2;
    const lineHeight = parseFloat(getComputedStyle(this.#content).lineHeight);
    if (!lineHeight) return;

    const isLonger = this.#content.scrollHeight > lineHeight * lines + 1;
    this.#toggle.hidden = !isLonger;
    if (!isLonger && this.hasAttribute('expanded')) this.#setExpanded(false);
  };
}

if (!customElements.get('clamped-text')) {
  customElements.define('clamped-text', ClampedText);
}
